"use client";

import { Loader2 } from "lucide-react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";

import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { Button } from "@/components/ui/button";

export default function ProfilePage() {
  const { data: session, update, status } = useSession();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (!session?.user) return;
    setName(session.user.name ?? "");

    // fetch phone from DB since session doesn't hold phone
    setLoadingProfile(true);
    fetch("/api/profile")
      .then((r) => r.json())
      .then((data) => {
        if (data?.user?.phone) setPhone(data.user.phone);
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, [session?.user?.id]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session?.user) return;
    setSaving(true);
    try {
      const response = await fetch("/api/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() || undefined })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? "Unable to save profile.");
      await update({ name: data.user.name });
      toast.success("Profile updated");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <DashboardShell title="Profile">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
        {status === "loading" || loadingProfile ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : !session?.user ? (
          <div className="text-center">
            <h2 className="text-2xl font-black tracking-normal">Sign in to manage profile</h2>
            <Link
              href="/login?callbackUrl=/dashboard/profile"
              className="mt-5 inline-flex h-11 items-center rounded-md bg-amazon-gold px-5 text-sm font-bold text-slate-950"
            >
              Sign in
            </Link>
          </div>
        ) : (
          <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
            <label>
              <span className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Name
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                className="h-11 w-full rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
              />
            </label>
            <label>
              <span className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Email
              </span>
              <input
                defaultValue={session.user.email ?? ""}
                disabled
                className="h-11 w-full rounded-md border border-slate-200 bg-slate-100 px-3 outline-none dark:border-white/10 dark:bg-white/10"
              />
            </label>
            <label>
              <span className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Phone
              </span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="h-11 w-full rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
              />
            </label>
            <label>
              <span className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Role
              </span>
              <input
                defaultValue={session.user.role}
                disabled
                className="h-11 w-full rounded-md border border-slate-200 bg-slate-100 px-3 outline-none dark:border-white/10 dark:bg-white/10"
              />
            </label>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save profile
              </Button>
            </div>
          </form>
        )}
      </div>
    </DashboardShell>
  );
}
